import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import { LawyerCard, LawyerCardSkeleton } from "@user/components/LawyerCard";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Search,
  ShieldCheck,
  MessageCircle,
  X,
  Heart,
  Home as HomeIcon,
  Gavel,
  Building2,
  Briefcase,
  Percent,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { lawyerPricing } from "@user/components/PriceTag";

const SORT_OPTIONS = [
  { value: "relevance", label: "Relevance" },
  { value: "rating_desc", label: "Rating: high to low" },
  { value: "experience_desc", label: "Experience: high to low" },
  { value: "price_asc", label: "Price: low to high" },
];

const POPULAR_AREAS = [
  { title: "Family", sub: "Divorce, custody", spec: "Family Law", icon: Heart, color: "#F472B6" },
  { title: "Property", sub: "Rent, disputes", spec: "Property Law", icon: HomeIcon, color: "#60A5FA" },
  { title: "Criminal", sub: "FIR, bail", spec: "Criminal Law", icon: Gavel, color: "#F59E0B" },
  { title: "Corporate", sub: "Business, MSME", spec: "Corporate Law", icon: Building2, color: "#10B981" },
  { title: "Employment", sub: "Job, salary", spec: "Labour Law", icon: Briefcase, color: "#EC4899" },
  { title: "Tax & GST", sub: "ITR, GST", spec: "Tax Law", icon: Percent, color: "#F97316" },
];

function FilterSwitch({ id, checked, onCheckedChange, label, activeClass = "bg-slate-50" }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-transparent ${activeClass}`}>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
      <Label htmlFor={id} className="text-xs font-semibold whitespace-nowrap cursor-pointer">
        {label}
      </Label>
    </div>
  );
}

export default function Lawyers() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [lawyers, setLawyers] = useState([]);
  const [cities, setCities] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [langs, setLangs] = useState([]);
  const [city, setCity] = useState("all");
  const [spec, setSpec] = useState(searchParams.get("spec") || "all");
  const [lang, setLang] = useState("all");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [liveOnly, setLiveOnly] = useState(false);
  const [livePulses, setLivePulses] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("relevance");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = {
        city: city !== "all" ? city : undefined,
        specialization: spec !== "all" ? spec : undefined,
        language: lang !== "all" ? lang : undefined,
        available_only: availableOnly || undefined,
        verified_only: verifiedOnly || undefined,
      };
      const [lr, mr, pr] = await Promise.all([
        api.get("/lawyers", { params }),
        api.get("/lawyers-meta"),
        api.get("/pulses/active", {
          params: {
            specialization: spec !== "all" ? spec : undefined,
            city: city !== "all" ? city : undefined,
          },
        }),
      ]);
      setLawyers(lr.data);
      setCities(mr.data.cities);
      setSpecs(mr.data.specializations);
      setLangs(mr.data.languages);
      setLivePulses(pr.data?.items || []);
    } catch (err) {
      console.warn("[lawyers] directory load failed", err?.response?.status || err?.message || err);
      setError(true);
    }
    setLoading(false);
  }, [city, spec, lang, availableOnly, verifiedOnly]);

  useEffect(() => { load(); }, [load]);

  // Refresh active pulses every 30s.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await api.get("/pulses/active", {
          params: {
            specialization: spec !== "all" ? spec : undefined,
            city: city !== "all" ? city : undefined,
          },
        });
        setLivePulses(r.data?.items || []);
      } catch (e) {
        console.warn("[lawyers] live pulses refresh failed", e);
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [spec, city]);

  const pulseById = useMemo(() => {
    const m = {};
    (livePulses || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [livePulses]);

  const enrichedLawyers = lawyers.map((l) => (pulseById[l.id] ? { ...l, ...pulseById[l.id] } : l));

  const filtered = useMemo(() => {
    let res = enrichedLawyers.filter((l) => {
      const matchesQ =
        !q.trim() ||
        (l.name + " " + l.specialization + " " + l.city + " " + (l.bio || "")).toLowerCase().includes(q.toLowerCase());
      const matchesLive = !liveOnly || !!pulseById[l.id];
      return matchesQ && matchesLive;
    });

    if (sortBy === "rating_desc") {
      res = [...res].sort((a, b) => (Number(b.rating || 0) - Number(a.rating || 0)));
    } else if (sortBy === "experience_desc") {
      res = [...res].sort((a, b) => (Number(b.experience || 0) - Number(a.experience || 0)));
    } else if (sortBy === "price_asc") {
      res = [...res].sort((a, b) => lawyerPricing(a, "video").offer - lawyerPricing(b, "video").offer);
    }
    return res;
  }, [enrichedLawyers, q, liveOnly, sortBy, pulseById]);

  const hasActiveFilters =
    city !== "all" || spec !== "all" || lang !== "all" || availableOnly || verifiedOnly || liveOnly || q.trim();

  const clearFilters = () => {
    setCity("all");
    setSpec("all");
    setLang("all");
    setAvailableOnly(false);
    setVerifiedOnly(false);
    setLiveOnly(false);
    setQ("");
    setSortBy("relevance");
    setSearchParams({}, { replace: true });
  };

  const onBook = (l) => {
    if (!user) { login(); return; }
    navigate(`/book/${l.id}`);
  };

  const onInstant = (l) => {
    if (!user) { login(); return; }
    navigate(`/book/${l.id}?mode=instant`);
  };

  const onViewProfile = (l) => navigate(`/lawyers/${l.id}`);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        {/* Heading + trust panel */}
        <div className="grid lg:grid-cols-12 gap-6 mb-6">
          <div className="lg:col-span-8">
            <span className="adv-chip">Verified Lawyers</span>
            <h1
              className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0A2540]"
              style={{ fontFamily: "Poppins" }}
            >
              Find the right lawyer for your legal issue
            </h1>
            <p className="mt-2 text-slate-600 max-w-2xl">
              Compare verified profiles, experience, consultation fees and availability.
            </p>
          </div>
          <div className="lg:col-span-4 flex items-start">
            <div className="bg-[#FBF4DF] border border-[#F1E3B5] rounded-2xl p-4 flex items-start gap-3 w-full">
              <ShieldCheck className="text-[#D4AF37] shrink-0" size={24} />
              <div>
                <p className="font-bold text-[#0A2540]">Verified &amp; Trusted</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  All listed lawyers are verified professionals with active, approved profiles.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <form
          onSubmit={(e) => e.preventDefault()}
          className="bg-white border border-slate-200 rounded-2xl p-2 flex items-center gap-2 shadow-sm"
        >
          <Search size={18} className="ml-3 text-slate-400 shrink-0" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by lawyer name, legal issue or keyword (e.g. divorce, property dispute, GST)"
            data-testid="lawyer-search-input"
            className="border-0 shadow-none focus-visible:ring-0 flex-1 text-[#0A2540] placeholder:text-slate-400"
          />
          <Button type="submit" className="rounded-xl adv-btn-gold font-bold px-6 h-10">
            Search
          </Button>
        </form>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="w-[150px] bg-white" data-testid="lawyer-filter-city">
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {cities.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={spec} onValueChange={setSpec}>
            <SelectTrigger className="w-[180px] bg-white" data-testid="lawyer-filter-spec">
              <SelectValue placeholder="Practice area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All practice areas</SelectItem>
              {specs.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="lawyer-filter-lang">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All languages</SelectItem>
              {langs.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <FilterSwitch
            id="avail"
            checked={availableOnly}
            onCheckedChange={setAvailableOnly}
            label="Available now"
            activeClass="bg-emerald-50 border-emerald-100"
          />
          <FilterSwitch
            id="verified"
            checked={verifiedOnly}
            onCheckedChange={setVerifiedOnly}
            label="Verified only"
            activeClass="bg-[#FBF4DF] border-[#F1E3B5]"
          />
          <FilterSwitch
            id="live"
            checked={liveOnly}
            onCheckedChange={setLiveOnly}
            label="Live consultation"
            activeClass="bg-emerald-50 border-emerald-100"
          />

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#1E3A8A] hover:text-[#0A2540] px-2 py-2"
              data-testid="clear-filters"
            >
              <X size={12} /> Clear all filters
            </button>
          )}
        </div>

        {/* Live pulse banner */}
        {livePulses.length > 0 && !liveOnly && (
          <div className="mt-4 bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-2xl p-3 sm:p-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600" />
              </span>
              <p className="text-sm font-bold text-emerald-900">
                {livePulses.length} lawyer{livePulses.length === 1 ? "" : "s"} available <strong>right now</strong>
                {spec !== "all" ? <> in {spec}</> : null}
                {city !== "all" ? <> · {city}</> : null}
              </p>
            </div>
            <button
              onClick={() => setLiveOnly(true)}
              className="ml-auto px-3 py-1.5 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Show only live →
            </button>
          </div>
        )}

        {/* Main content + sidebar */}
        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-9">
            {/* Results header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm font-semibold text-slate-600">
                {loading ? "Loading lawyers…" : `${filtered.length} lawyer${filtered.length === 1 ? "" : "s"} found`}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Sort by</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[150px] bg-white" data-testid="lawyer-sort-by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Results */}
            <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(360px,1fr))] gap-4" data-testid="lawyers-list">
              {loading && Array.from({ length: 6 }).map((_, i) => <LawyerCardSkeleton key={i} />)}

              {!loading && error && (
                <div className="md:col-span-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
                  <p className="text-sm text-slate-500">Could not load lawyers right now.</p>
                  <Button onClick={load} variant="outline" className="mt-3 rounded-full font-semibold text-xs h-9">
                    Try again
                  </Button>
                </div>
              )}

              {!loading && !error && filtered.length === 0 && (
                <div className="md:col-span-full bg-white border border-slate-200 rounded-2xl p-10 text-center">
                  <p className="text-slate-500">No lawyers match your search.</p>
                  {hasActiveFilters && (
                    <Button onClick={clearFilters} className="mt-3 rounded-full adv-btn-gold font-bold text-xs h-9">
                      Clear filters
                    </Button>
                  )}
                </div>
              )}

              {!loading && !error && filtered.map((l) => (
                <LawyerCard key={l.id} lawyer={l} onBook={onBook} onInstant={onInstant} onViewProfile={onViewProfile} />
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-3 space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <h3 className="font-bold text-[#0A2540] mb-3" style={{ fontFamily: "Poppins" }}>
                Popular Practice Areas
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {POPULAR_AREAS.map((a) => (
                  <Link
                    key={a.spec}
                    to={`/lawyers?spec=${encodeURIComponent(a.spec)}`}
                    className="group text-center p-2 rounded-xl border border-slate-100 hover:border-[#D4AF37]/40 hover:bg-[#FBF4DF]/40 transition-all"
                  >
                    <div
                      className="h-9 w-9 rounded-lg grid place-items-center mx-auto"
                      style={{ background: `${a.color}20`, color: a.color }}
                    >
                      <a.icon size={18} />
                    </div>
                    <p className="mt-2 text-[11px] font-bold text-[#0A2540]">{a.title}</p>
                    <p className="text-[9px] text-slate-500 truncate">{a.sub}</p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-[#FBF4DF] border border-[#F1E3B5] rounded-2xl p-4">
              <MessageCircle className="text-[#D4AF37]" size={24} />
              <h3 className="mt-2 font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
                Need help finding the right lawyer?
              </h3>
              <p className="mt-1 text-xs text-slate-600">
                Tell us your issue and we'll suggest the best lawyers for you.
              </p>
              <Link to="/chat" className="block mt-3">
                <Button className="w-full rounded-full adv-btn-gold font-bold h-10">
                  Get AI Guidance
                </Button>
              </Link>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
